import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSnackbar } from 'notistack';
import axiosInstance from '../services/axiosInstance';

import Header from '../components/Header';
import Footer from '../components/Footer';
import MuiUpload from '../components/MuiUpload';
import AutocompleteCreatableSelectField from '../components/ControlledAutocomplete';
import ControlledAutocomplete from '../components/ControlledAutocomplete';

import {
    Box,
    Container,
    Grid,
    Button,
    Tabs,
    Tab,
    Typography,
    Paper,
    TextField,
    FormControlLabel,
    Checkbox,
    Link,
    Divider,
    Card,
    CardHeader,
    Stack,
    Backdrop,
    CircularProgress,
    IconButton,
    CardContent,
} from '@mui/material';

// --- Icons ---
import DeleteIcon from '@mui/icons-material/Delete';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import SettingsIcon from '@mui/icons-material/Settings';
import SpeedIcon from '@mui/icons-material/Speed';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import RichTextEditor from '../components/RichTextEditor';



// import Field from '../components/Field';
// TODO: Implement or replace Field component usage below, or remove usages if not needed.


interface SellPageProps {
    mode: 'light' | 'dark';
    setMode: (mode: 'light' | 'dark') => void;
}
export const VehicleSalesInfoSchema = z.object({
    askingPrice: z.coerce.number().optional(),
    mileage: z.coerce.number().optional(),
    vehicleFeatures: z
        .string()
        .max(2000, 'Vehicle features must be 2000 characters or fewer')
        .optional(),
    accessories: z
        .string()
        .max(2000, 'Accessories must be 2000 characters or fewer')
        .optional(),
    description: z
        .string()
        .max(2000, 'Description must be 2000 characters or fewer')
        .optional(),
    blockCarplateFromPhotos: z.boolean().optional(),
});

export const NewVehicleSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    contactNo: z.string().min(8, 'Please enter a valid contact number (at least 8 characters)'),
    email: z.string().email('Invalid email address'),
    address: z.string().min(1, 'Name is required'),
    ownerId: z.string().min(1, { message: 'Owner ID is required!' }),

    carplateNo: z.string().min(1, { message: 'Carplate Number is required!' }),
    accessCode: z.string().min(1, { message: 'Access Code is required!' }),

    vehicleBrand: z.union([
        z.string().min(1, { message: 'Vehicle Brand is required!' }),
        z.number().min(1, { message: 'Vehicle Brand is required!' }),
    ]),
    vehicleModelBrand: z.union([
        z.string().min(1, { message: 'Vehicle Model is required!' }),
        z.number().min(1, { message: 'Vehicle Model is required!' }),
    ]),
    vehicleTransmissionType: z.union([
        z.string().min(1, { message: 'Transmission Type is required!' }),
        z.number().min(1, { message: 'Transmission Type is required!' }),
    ]),
    vehicleFuelType: z.union([
        z.string().min(1, { message: 'Fuel Type is required!' }),
        z.number().min(1, { message: 'Fuel Type is required!' }),
    ]),
    vehicleBodyType: z.union([
        z.string().min(1, { message: 'Body Type is required!' }),
        z.number().min(1, { message: 'Body Type is required!' }),
    ]),

    seatCapacity: z.coerce.number().optional(),

    vehicleSalesInfo: VehicleSalesInfoSchema.optional(),

    vehicleImageName: z
        .array(z.union([z.instanceof(File), z.string()]))
        .min(1, { message: 'Vehicle image is required' }),

    vehicleVideoName: z
        .array(z.union([z.instanceof(File), z.string()]))
        .max(1, { message: 'Only one vehicle video is allowed!' })
        .optional(),
});


export type NewVehicleSchemaType = z.infer<typeof NewVehicleSchema>;

const defaultValues: NewVehicleSchemaType = {
    name: '',
    contactNo: '',
    email: '',
    address: '',
    carplateNo: '',
    accessCode: '',
    ownerId: '',
    // vehicleBrandId: 0,
    // vehicleTransmissionTypeId: 0,
    // vehicleFuelTypeId: 0,
    // vehicleBodyTypeId: 0,
    vehicleBrand: '',
    vehicleModelBrand: '',
    vehicleTransmissionType: '',
    vehicleFuelType: '',
    vehicleBodyType: '',
    seatCapacity: 0,
    vehicleSalesInfo: {
        askingPrice: 0,
        mileage: 0,
        vehicleFeatures: '',
        accessories: '',
        description: '',
        blockCarplateFromPhotos: false,
    },
    vehicleImageName: [],
    vehicleVideoName: [],
};


const SellPage = ({ mode, setMode }: SellPageProps) => {
    const { enqueueSnackbar } = useSnackbar();

    const navigate = useNavigate();

    const [isLoggedIn] = useState<boolean>(false);
    const [adType, setAdType] = useState<'standard' | 'premium'>('standard');
    const [tabIndex, setTabIndex] = useState(0);
    const [loading, setLoading] = useState(false);

    const [vehicleBodyType, setVehicleBodyType] = useState<
        { vehicleBodyTypeId: number; description: string }[]
    >([]);
    const [vehicleFuelType, setVehicleFuelType] = useState<
        { vehicleFuelTypeId: number; description: string }[]
    >([]);
    const [vehicleTransmissionType, setVehicleTransmissionType] = useState<
        { vehicleTransmissionTypeId: number; description: string }[]
    >([]);
    const [vehicleBrand, setVehicleBrand] = useState<
        { vehicleBrandId: number; description: string }[]
    >([]);
    const [vehicleModelBrand, setVehicleModelBrand] = useState<
        { vehicleModelId: number; description: string; vehicleBrandId: number }[]
    >([]);

    const [dealerData, setDealerData] = useState<any[]>([]);

    const methods = useForm<NewVehicleSchemaType>({
        mode: 'onSubmit',
        resolver: zodResolver(NewVehicleSchema),
        defaultValues,
    });


    const {
        reset,
        watch,
        setValue,
        control,
        handleSubmit,
        formState: { isSubmitting, isValid, errors },
    } = methods;


    const onSubmit = handleSubmit(async (data) => {
        try {
            setLoading(true);

            // -----------------
            // Validate required images
            // -----------------
            if (!data.vehicleImageName || data.vehicleImageName.length === 0) {
                enqueueSnackbar("Please upload at least one vehicle image.", { variant: "error" });
                return; // stop submission
            }

            // -----------------
            // Build FormData
            // -----------------
            const formData = new FormData();

            // Dealer Info
            formData.append("Name", data.name);
            formData.append("ContactNo", data.contactNo);
            formData.append("Email", data.email);
            formData.append("Address", data.address);

            // Vehicle Info
            formData.append("OwnerId", data.ownerId ?? "");
            formData.append("CarplateNo", data.carplateNo ?? "");
            formData.append("AccessCode", data.accessCode ?? "");
            formData.append("VehicleBrand", String(data.vehicleBrand ?? ""));
            formData.append("VehicleModelBrand", String(data.vehicleModelBrand ?? ""));
            formData.append("VehicleTransmissionType", String(data.vehicleTransmissionType ?? ""));
            formData.append("VehicleFuelType", String(data.vehicleFuelType ?? ""));
            formData.append("VehicleBodyType", String(data.vehicleBodyType ?? ""));
            formData.append("SeatCapacity", String(data.seatCapacity ?? ""));

            // Vehicle Sales Info
            if (data.vehicleSalesInfo) {
                formData.append("AskingPrice", String(data.vehicleSalesInfo.askingPrice ?? ""));
                formData.append("Mileage", String(data.vehicleSalesInfo.mileage ?? ""));
                formData.append("VehicleFeatures", data.vehicleSalesInfo.vehicleFeatures ?? "");
                formData.append("Accessories", data.vehicleSalesInfo.accessories ?? "");
                formData.append("Description", data.vehicleSalesInfo.description ?? "");
                formData.append("BlockCarplateFromPhotos", String(data.vehicleSalesInfo.blockCarplateFromPhotos ?? false));
            }

            // Upload Images
            if (data.vehicleImageName?.length > 0) {
                data.vehicleImageName.forEach((file: File | string, idx: number) => {
                    if (file instanceof File) {
                        formData.append("UploadedImages", file);
                    } else {
                        formData.append(`ExistingImageUrls[${idx}]`, file);
                    }
                });
            }

            // Upload Videos
            if (data.vehicleVideoName && data.vehicleVideoName.length > 0) {
                data.vehicleVideoName.forEach((file: File | string, idx: number) => {
                    if (file instanceof File) {
                        formData.append("UploadedVideos", file);
                    } else if (file) {
                        formData.append(`ExistingVideoUrls[${idx}]`, file);
                    }
                });
            }

            // -----------------
            // Send ONE API request
            // -----------------
            const response = await axiosInstance.post(
                "/CustomerVehicle/CustomerVehicleCreate",
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            const result = response.data as {
                responseCode: string;
                responseMessage?: string;
            };

            if (result.responseCode === "00") {
                reset(defaultValues);
                navigate('/vehicle-success');
            } else {
                enqueueSnackbar(result.responseMessage || "Failed to create vehicle", {
                    variant: "error",
                });
            }
        } catch (error) {
            enqueueSnackbar("An error occurred while creating the vehicle.", {
                variant: "error",
            });
            console.error("Error in onSubmit:", error);
        } finally {
            setLoading(false);
        }
    });



    const payload = {
        vehicleBodyTypeData: true,
        vehicleFuelTypeData: true,
        vehicleTransmissionTypeData: true,
        vehicleBrandData: true,
        vehicleModelBrandData: true,
        dealerData: true,

    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axiosInstance.post(
                    '/CustomerVehicle/VehicleMultipleDataList',
                    payload,
                );
                const data = (response.data as { responseData: any }).responseData;
                setVehicleBodyType(data?.vehicleBodyTypeData || []);
                setVehicleFuelType(data?.vehicleFuelTypeData || []);
                setVehicleTransmissionType(data?.vehicleTransmissionTypeData || []);
                setVehicleBrand(data?.vehicleBrandData || []);
                setVehicleModelBrand(data?.vehicleModelBrandData || []);
                setDealerData(
                    (data?.dealerData || [])
                        .filter((dealer: any) => dealer.dealerTypeId === 2) // only dealerTypeId 2
                        .map((dealer: any) => ({
                            dealerId: dealer.dealerId,
                            dealerName: dealer.dealerName,
                            contactNo: dealer.contactNo,
                            email: dealer.email,
                            address: dealer.address,
                            dealerTypeId: dealer.dealerTypeId,
                            dealerTypeName: dealer.dealerTypeName,
                        }))
                );


                // console.log('Fetched data:', data);
            } catch (error) {
                console.error('Failed to fetch data', error);
            }
        };

        fetchData();
    }, []);

    const values = watch();


    const renderVehicleDetails = () => (
        <>
            <Divider sx={{ my: 4 }} />

            <Stack spacing={4}>
                {/* 🚗 --- Basic Vehicle Information --- 🚗 */}
                <Card
                    variant="elevation"
                    sx={{
                        borderRadius: 3,
                        p: 4,
                        boxShadow: 3, // Subtle shadow for a lift effect
                        bgcolor: 'background.paper', // Ensures the card background is white/light
                    }}
                >
                    <Typography
                        variant="h5"
                        color="primary.main"
                        sx={{
                            mb: 3,
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            borderBottom: 1,
                            borderColor: 'divider',
                            pb: 1.5,
                        }}
                    >
                        <DirectionsCarIcon fontSize="medium" color="primary" /> Basic Vehicle Information
                    </Typography>

                    <Grid container spacing={3}>
                        {/* Carplate Number */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                name="carplateNo"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Carplate Number"
                                        placeholder="e.g., ABC 1234"
                                        fullWidth
                                        variant="outlined"
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }} // Enforce white background
                                        error={!!errors.carplateNo}
                                        helperText={errors.carplateNo?.message}
                                        onChange={(e) => {
                                            const upperTrimmed = e.target.value.toUpperCase().replace(/\s+/g, '');
                                            setValue('carplateNo', upperTrimmed);
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Owner ID */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                name="ownerId"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Owner ID"
                                        placeholder="Enter Owner ID"
                                        fullWidth
                                        variant="outlined"
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }} // Enforce white background
                                        error={!!errors.ownerId}
                                        helperText={errors.ownerId?.message}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Access Code */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                name="accessCode"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Access Code"
                                        placeholder="Enter Access Code"
                                        fullWidth
                                        variant="outlined"
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }} // Enforce white background
                                        error={!!errors.accessCode}
                                        helperText={errors.accessCode?.message}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Vehicle Brand */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <AutocompleteCreatableSelectField
                                name="vehicleBrand"
                                control={control}
                                label="Vehicle Brand"
                                placeholder="Select or enter Vehicle Brand"
                                options={vehicleBrand.map((o) => ({
                                    value: o.vehicleBrandId,
                                    label: o.description,
                                }))}

                                onChangeCallback={() => setValue('vehicleModelBrand', '')}
                            />
                        </Grid>

                        {/* Vehicle Model */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <AutocompleteCreatableSelectField
                                name="vehicleModelBrand"
                                control={control}
                                label="Vehicle Model"
                                placeholder="Select or enter Vehicle Model"
                                disabled={!watch('vehicleBrand')}
                                options={vehicleModelBrand
                                    .filter((model) =>
                                        watch('vehicleBrand')
                                            ? model.vehicleBrandId === Number(watch('vehicleBrand'))
                                            : true
                                    )
                                    .map((o) => ({
                                        value: o.vehicleModelId,
                                        label: o.description,
                                    }))}

                            />
                        </Grid>

                        {/* Vehicle Body Type */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <AutocompleteCreatableSelectField
                                name="vehicleBodyType"
                                control={control}
                                label="Body Type"
                                placeholder="Select or enter Body Type"
                                options={vehicleBodyType.map((o) => ({
                                    value: o.vehicleBodyTypeId,
                                    label: o.description,
                                }))}
                            />
                        </Grid>

                        {/* Vehicle Fuel Type */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <AutocompleteCreatableSelectField
                                name="vehicleFuelType"
                                control={control}
                                label="Fuel Type"
                                placeholder="Select or enter Fuel Type"
                                options={vehicleFuelType.map((o) => ({
                                    value: o.vehicleFuelTypeId,
                                    label: o.description,
                                }))}
                            />
                        </Grid>

                        {/* Vehicle Transmission Type */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <AutocompleteCreatableSelectField
                                name="vehicleTransmissionType"
                                control={control}
                                label="Transmission Type"
                                placeholder="Select or enter Transmission"
                                options={vehicleTransmissionType.map((o) => ({
                                    value: o.vehicleTransmissionTypeId,
                                    label: o.description,
                                }))}
                            />
                        </Grid>


                    </Grid>
                </Card>


                <Grid container spacing={3}>

                </Grid>

                {/* 💰 --- Sales Information --- 💰 */}
                <Card
                    variant="elevation"
                    sx={{
                        borderRadius: 3,
                        p: 4,
                        boxShadow: 3,
                        bgcolor: 'background.paper',
                    }}
                >
                    <Typography
                        variant="h5"
                        color="success.main"
                        sx={{
                            mb: 3,
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            borderBottom: 1,
                            borderColor: 'divider',
                            pb: 1.5,
                        }}
                    >
                        <AttachMoneyIcon fontSize="medium" color="success" /> Sales Information
                    </Typography>

                    <Grid container spacing={3}>
                        {/* Asking Price */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                name="vehicleSalesInfo.askingPrice"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <TextField
                                        label="Asking Price (RM)"
                                        value={(value ?? 0).toFixed(2)}
                                        fullWidth
                                        variant="outlined"
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }} // Enforce white background
                                        error={!!errors.vehicleSalesInfo?.askingPrice}
                                        helperText={errors.vehicleSalesInfo?.askingPrice?.message}
                                        onChange={(e) => {
                                            const digits = e.target.value.replace(/\D/g, '');
                                            const cents = digits === '' ? 0 : parseInt(digits, 10);
                                            onChange(parseFloat((cents / 100).toFixed(2)));
                                        }}
                                        inputMode="numeric"
                                    />
                                )}
                            />
                        </Grid>

                        {/* Mileage */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                name="vehicleSalesInfo.mileage"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Mileage (km)"
                                        placeholder="e.g., 50000"
                                        type="number"
                                        fullWidth
                                        variant="outlined"
                                        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }} // Enforce white background
                                        error={!!errors.vehicleSalesInfo?.mileage}
                                        helperText={errors.vehicleSalesInfo?.mileage?.message}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Block Carplate From Photos Checkbox */}
                        <Grid size={{ xs: 12 }}>
                            <Controller
                                name="vehicleSalesInfo.blockCarplateFromPhotos"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                {...field}
                                                checked={field.value || false}
                                                color="primary"
                                            />
                                        }
                                        label={<Typography variant="body1">Block Carplate From Photos (Recommended for privacy)</Typography>}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </Card>

                {/* 📝 --- Additional Details --- 📝 */}
                <Card
                    variant="elevation"
                    sx={{
                        borderRadius: 3,
                        p: 4,
                        boxShadow: 3,
                        bgcolor: 'background.paper',
                    }}
                >
                    <Typography
                        variant="h5"
                        color="info.main"
                        sx={{
                            mb: 3,
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            borderBottom: 1,
                            borderColor: 'divider',
                            pb: 1.5,
                        }}
                    >
                        <SpeedIcon fontSize="medium" color="info" /> Additional Details
                    </Typography>

                    <Stack spacing={4}>
                        {/* Description */}
                        <Controller
                            name="vehicleSalesInfo.description"
                            control={control}
                            render={({ field }) => (
                                <Box>
                                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                        Vehicle Description
                                    </Typography>

                                    <RichTextEditor
                                        value={field.value ?? ''}
                                        onChange={field.onChange}
                                    // Assuming RichTextEditor is styled internally, 
                                    // but you can add a wrapping Box style if needed.
                                    />

                                    {errors.vehicleSalesInfo?.description && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                            {errors.vehicleSalesInfo.description.message}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        />

                        {/* Vehicle Features */}
                        <Controller
                            name="vehicleSalesInfo.vehicleFeatures"
                            control={control}
                            render={({ field }) => (
                                <Box>
                                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                        Key Vehicle Features
                                    </Typography>

                                    <RichTextEditor
                                        value={field.value ?? ''}
                                        onChange={field.onChange}
                                    />

                                    {errors.vehicleSalesInfo?.vehicleFeatures && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                            {errors.vehicleSalesInfo.vehicleFeatures.message}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        />

                        {/* Accessories */}
                        <Controller
                            name="vehicleSalesInfo.accessories"
                            control={control}
                            render={({ field }) => (
                                <Box>
                                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                        Accessories Included
                                    </Typography>

                                    <RichTextEditor
                                        value={field.value ?? ''}
                                        onChange={field.onChange}
                                    />

                                    {errors.vehicleSalesInfo?.accessories && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                            {errors.vehicleSalesInfo.accessories.message}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        />
                    </Stack>
                </Card>
            </Stack>
        </>
    );


    const handleRemoveVideoFile = useCallback(
        (inputFile: File | string) => {
            const filtered = values.vehicleVideoName?.filter((file) => file !== inputFile) || [];
            setValue('vehicleVideoName', filtered);
        },
        [setValue, values.vehicleVideoName]
    );

    const handleRemoveAllVideoFiles = useCallback(() => {
        setValue('vehicleVideoName', []);
    }, [setValue]);

    const handleRemoveFile = useCallback(
        (inputFile: File | string) => {
            const filtered = values.vehicleImageName?.filter((file) => file !== inputFile) || [];
            setValue('vehicleImageName', filtered);
        },
        [setValue, values.vehicleImageName]
    );

    const handleRemoveAllFiles = useCallback(() => {
        setValue('vehicleImageName', []);
    }, [setValue]);

    useEffect(() => {
        if (errors.vehicleImageName) {
            enqueueSnackbar(errors.vehicleImageName.message, { variant: 'error' });
        }
    }, [errors.vehicleImageName, enqueueSnackbar]);

    const renderUploads = () => (
        <Stack spacing={4} sx={{ p: 3 }}>
            {/* ---- IMAGE UPLOAD SECTION ---- */}
            <Card
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    boxShadow: 1,
                    overflow: 'hidden',
                }}
            >
                <CardHeader
                    title="Vehicle Images"
                    subheader="Upload clear, high-quality photos of the vehicle"
                    titleTypographyProps={{ fontWeight: 600, fontSize: '1.05rem' }}
                    subheaderTypographyProps={{ color: 'text.secondary', fontSize: '0.875rem' }}
                />
                <Divider />
                <CardContent>
                    <MuiUpload
                        label="Upload Images"
                        multiple
                        accept="image/*"
                        maxSize={5 * 1024 * 1024}
                        files={values.vehicleImageName || []}
                        onChange={(newFiles) => setValue('vehicleImageName', newFiles)}
                        onRemove={handleRemoveFile}
                        onRemoveAll={handleRemoveAllFiles}
                    />

                    {/* Error Message */}
                    {errors.vehicleImageName && (
                        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                            {errors.vehicleImageName.message}
                        </Typography>
                    )}

                    {/* Tips */}
                    <Stack spacing={0.5} sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            • Supported formats: JPG, PNG
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            • Max file size: 5MB per image
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            • Recommended ratio: 16:12 (e.g., 1024×768 or 1600×1200)
                        </Typography>
                    </Stack>
                </CardContent>
            </Card>

            {/* ---- VIDEO UPLOAD SECTION ---- */}
            <Card
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    boxShadow: 1,
                    overflow: 'hidden',
                }}
            >
                <CardHeader
                    title="Vehicle Video"
                    subheader="Upload a short video showcasing the car’s condition"
                    titleTypographyProps={{ fontWeight: 600, fontSize: '1.05rem' }}
                    subheaderTypographyProps={{ color: 'text.secondary', fontSize: '0.875rem' }}
                />
                <Divider />
                <CardContent>
                    <MuiUpload
                        label="Upload Video"
                        accept="video/*"
                        multiple={false}
                        maxFiles={1}
                        replaceOnUpload
                        maxSize={100 * 1024 * 1024}
                        files={values.vehicleVideoName || []}
                        onChange={(newFiles) => setValue('vehicleVideoName', newFiles)}
                        onRemove={handleRemoveVideoFile}
                        onRemoveAll={handleRemoveAllVideoFiles}
                    />

                    {/* Video Tips */}
                    <Stack spacing={0.5} sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            • Supported formats: MP4, AVI
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            • Max file size: 100MB
                        </Typography>
                    </Stack>
                </CardContent>
            </Card>
        </Stack>
    );


    // Watch email and contactNo fields
    // inside your component
    const watchEmail = watch("email");
    const watchContact = watch("contactNo");

    const [locked, setLocked] = useState(false);
    const [autoFilled, setAutoFilled] = useState(false); // 👈 new flag

    const dealerMap = useMemo(() => {
        const map = new Map<string, typeof dealerData[0]>();
        dealerData.forEach((dealer) => {
            if (dealer.email) map.set(dealer.email, dealer);
            if (dealer.contactNo) map.set(dealer.contactNo, dealer);
        });
        return map;
    }, [dealerData]);

    useEffect(() => {
        // Case 1: user clears both fields → reset everything
        if (!watchEmail && !watchContact) {
            setLocked(false);
            setAutoFilled(false); // reset the autofill lock
            return;
        }

        const matchedDealer = dealerMap.get(watchEmail) || dealerMap.get(watchContact);

        // Case 2: found a match AND not yet autofilled
        if (matchedDealer && !autoFilled) {
            // Fill once
            if (watchContact && !watchEmail) {
                setValue("email", matchedDealer.email, { shouldValidate: true });
            }

            if (watchEmail && !watchContact) {
                setValue("contactNo", matchedDealer.contactNo, { shouldValidate: true });
            }

            setValue("name", matchedDealer.dealerName, { shouldValidate: true });
            setValue("address", matchedDealer.address, { shouldValidate: true });

            setLocked(true);
            setAutoFilled(true); // 👈 mark autofill done
            return;
        }

        // Case 3: user modifies email/contact after autofill → unlock form
        if (!matchedDealer && autoFilled) {
            setLocked(false);
            setAutoFilled(false);
        }
    }, [watchEmail, watchContact, dealerMap, autoFilled, setValue]);








    return (
        <>
            <Header  />

            <Container maxWidth="lg" sx={{ py: 4}}>
                <Box sx={{ maxWidth: 900, mx: "auto", mt: 8 }}> {/* <-- Constrain width */}

                    {/* Ad Type Selection */}
                    <Grid container spacing={2} mb={2}>
                        <Backdrop
                            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                            open={loading}
                        >
                            <CircularProgress color="inherit" />
                        </Backdrop>

                        {/* Standard Ad */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    borderColor: "grey.300",
                                    borderWidth: 2,
                                    borderRadius: 2,
                                    cursor: "default", // no pointer
                                    transition: "none", // disable hover transitions
                                    "&:hover": {
                                        borderColor: "grey.300", // stay the same
                                        boxShadow: "none", // disable shadow
                                    },
                                }}
                            >
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Standard
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                    List your car till it&apos;s sold!
                                </Typography>

                                <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                                    <Typography variant="h6" fontWeight="bold">
                                        $72
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Premium Ad */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    borderColor: "grey.300",
                                    borderWidth: 2,
                                    borderRadius: 2,
                                    cursor: "default",
                                    transition: "none",
                                    "&:hover": {
                                        borderColor: "grey.300",
                                        boxShadow: "none",
                                    },
                                }}
                            >
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        Premium
                                    </Typography>
                                    <Typography variant="caption" color="error">
                                        ✨ Get over 100,000 views a day
                                    </Typography>
                                </Box>

                                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                    Top priority search ranking!
                                </Typography>

                                <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                                    <Typography variant="h6" fontWeight="bold">
                                        $112
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>



                    {/* Form Section */}
                    <FormProvider {...methods}>
                        <form onSubmit={onSubmit}>
                            {/* Personal Info */}
                            <Card
                                variant="outlined"
                                sx={{
                                    borderRadius: 2,
                                    boxShadow: 1,
                                    mb: 3,
                                }}
                            >
                                <CardHeader
                                    title="Personal Information"
                                    subheader="Fill in your personal details below"
                                    titleTypographyProps={{ fontWeight: 600, fontSize: '1.05rem' }}
                                    subheaderTypographyProps={{ color: 'text.secondary', fontSize: '0.875rem' }}
                                    action={
                                        <Typography color="error" fontSize="0.85rem" fontWeight="medium">
                                            REQUIRED
                                        </Typography>
                                    }
                                    sx={{
                                        bgcolor: 'grey.50',
                                        borderBottom: '1px solid',
                                        borderColor: 'grey.200',
                                        py: 1.5,
                                    }}
                                />

                                <CardContent sx={{ pt: 2 }}>
                                    <Grid container spacing={2.5} alignItems="flex-start">
                                        {/* Contact */}
                                        <Grid size={{ xs: 12, md: 3 }}>
                                            <Typography variant="body2" fontWeight={500}>
                                                Contact Number
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 9 }}>
                                            <Box sx={{ maxWidth: 400 }}>
                                                <Controller
                                                    name="contactNo"
                                                    control={methods.control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            size="small"
                                                            error={!!methods.formState.errors.contactNo}
                                                            helperText={methods.formState.errors.contactNo?.message}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'white',
                                                                },
                                                                // The .MuiInputBase-input selector is sometimes needed for legacy TextField variants
                                                                '& .MuiInputBase-input': {
                                                                    backgroundColor: 'white',
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        </Grid>

                                        {/* Email */}
                                        <Grid size={{ xs: 12, md: 3 }}>
                                            <Typography variant="body2" fontWeight={500}>
                                                Email
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 9 }}>
                                            <Box sx={{ maxWidth: 400 }}>
                                                <Controller
                                                    name="email"
                                                    control={methods.control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            size="small"
                                                            error={!!methods.formState.errors.email}
                                                            helperText={methods.formState.errors.email?.message}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'white',
                                                                },
                                                                // The .MuiInputBase-input selector is sometimes needed for legacy TextField variants
                                                                '& .MuiInputBase-input': {
                                                                    backgroundColor: 'white',
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        </Grid>

                                        {/* Name */}
                                        <Grid size={{ xs: 12, md: 3 }}>
                                            <Typography variant="body2" fontWeight={500}>
                                                Full Name
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 9 }}>
                                            <Box sx={{ maxWidth: 400 }}>
                                                <Controller
                                                    name="name"
                                                    control={methods.control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            size="small"
                                                            disabled={locked}
                                                            error={!!methods.formState.errors.name}
                                                            helperText={methods.formState.errors.name?.message}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'white',
                                                                },
                                                                // The .MuiInputBase-input selector is sometimes needed for legacy TextField variants
                                                                '& .MuiInputBase-input': {
                                                                    backgroundColor: 'white',
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        </Grid>

                                        {/* Address */}
                                        <Grid size={{ xs: 12, md: 3 }}>
                                            <Typography variant="body2" fontWeight={500}>
                                                Address
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 9 }}>
                                            <Box sx={{ maxWidth: 400 }}>
                                                <Controller
                                                    name="address"
                                                    control={methods.control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            size="small"
                                                            multiline
                                                            rows={3}
                                                            disabled={locked}
                                                            error={!!methods.formState.errors.address}
                                                            helperText={methods.formState.errors.address?.message}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'white',
                                                                },
                                                                // The .MuiInputBase-input selector is sometimes needed for legacy TextField variants
                                                                '& .MuiInputBase-input': {
                                                                    backgroundColor: 'white',
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* Tabs */}

                            <Tabs
                                value={tabIndex}
                                onChange={(_, newValue) => setTabIndex(newValue)}
                                textColor="primary"
                                indicatorColor="primary"
                            >
                                <Tab label="Vehicle Details" />
                                <Tab label="Document Image" />
                            </Tabs>

                            <Box mt={2}>
                                {tabIndex === 0 && <Box>{renderVehicleDetails()}</Box>}
                                {tabIndex === 1 && <>{renderUploads()}</>}
                            </Box>

                            {/* Submit */}
                            <Box mt={3} display="flex" justifyContent="flex-end">
                                <Button type="submit" variant="contained" size="medium" disabled={loading}>
                                    {loading ? "Submitting..." : "Submit"}
                                </Button>
                            </Box>
                        </form>
                    </FormProvider>


                </Box>
            </Container>


            <Footer />
        </>
    );
};

export default SellPage;
